import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useLeadStore } from '../stores/leadStore';

const TEAM_MEMBERS = [
  'All Team Members',
  'Roisin Welsh',
  'Paraic Mooney',
  'Mark Mooney',
  'Matthew',
  'John Reilly',
  'Ian Callaghan',
  "Keith O'Brien",
  "Ruarc O'Donoghue",
  'Mark Power',
  'Lauren Woods',
  'Unassigned',
  'Other',
];

const STAGE_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'lost', label: 'Lost' },
  { value: 'won', label: 'Won' },
  { value: 'assigned', label: 'Assigned' },
];

const INTEREST_FILTERS = [
  { value: 'all', label: 'All Interest Levels' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const DEMO_REPORT_ROWS: ReportRow[] = [
  {
    teamMember: 'Roisin Welsh',
    leadsContacted: 32,
    leadsWon: 6,
    leadsLost: 4,
    conversionRate: Number(((6 / 32) * 100).toFixed(1)),
  },
  {
    teamMember: 'Paraic Mooney',
    leadsContacted: 28,
    leadsWon: 4,
    leadsLost: 5,
    conversionRate: Number(((4 / 28) * 100).toFixed(1)),
  },
  {
    teamMember: 'Mark Power',
    leadsContacted: 18,
    leadsWon: 3,
    leadsLost: 2,
    conversionRate: Number(((3 / 18) * 100).toFixed(1)),
  },
  {
    teamMember: 'All Team Members',
    leadsContacted: 120,
    leadsWon: 18,
    leadsLost: 14,
    conversionRate: Number(((18 / 120) * 100).toFixed(1)),
  },
];

interface ReportRow {
  teamMember: string;
  leadsContacted: number;
  leadsWon: number;
  leadsLost: number;
  conversionRate: number;
}

const isLeadLost = (status?: string) => {
  if (!status) return false;
  const normalised = status.toLowerCase();
  return normalised === 'no-interest' || normalised === 'lost' || normalised === 'not_interested';
};

const isLeadWon = (status?: string, originalStatus?: string) => {
  const normalised = status?.toLowerCase();
  const original = originalStatus?.toLowerCase();
  return normalised === 'appointment' || original === 'converted';
};

const isLeadContacted = (status?: string, originalStatus?: string) => {
  const normalised = status?.toLowerCase();
  const original = originalStatus?.toLowerCase();
  return (
    normalised === 'assigned' ||
    normalised === 'contacted' ||
    normalised === 'appointment' ||
    original === 'in_contact'
  );
};

const Reports: React.FC = () => {
  const { leads, isLoading, initializeLeads } = useLeadStore();
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([TEAM_MEMBERS[0]]);
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [interestFilter, setInterestFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    if (!leads.length) {
      initializeLeads();
    }
  }, [initializeLeads, leads.length]);

  const handleRefresh = () => {
    initializeLeads();
  };

  const filteredReport = useMemo<ReportRow[]>(() => {
    const teamKeys = new Set(
      selectedTeamMembers.includes('All Team Members')
        ? TEAM_MEMBERS
        : selectedTeamMembers
    );

    const rows: Record<string, ReportRow> = {};

    const ensureRow = (name: string) => {
      if (!rows[name]) {
        rows[name] = {
          teamMember: name,
          leadsContacted: 0,
          leadsWon: 0,
          leadsLost: 0,
          conversionRate: 0,
        };
      }
      return rows[name];
    };

    const matchesFilters = (lead: any) => {
      const updatedAt = lead.updatedAt || lead.lastContact;
      if (startDate) {
        if (!updatedAt || new Date(updatedAt) < new Date(startDate)) {
          return false;
        }
      }
      if (endDate) {
        if (!updatedAt || new Date(updatedAt) > new Date(endDate)) {
          return false;
        }
      }

      if (stageFilter !== 'all') {
        const status = lead.stage?.toLowerCase();
        if (stageFilter === 'contacted' && !isLeadContacted(status, lead.originalStatus)) {
          return false;
        }
        if (stageFilter === 'lost' && !isLeadLost(status || lead.originalStatus)) {
          return false;
        }
        if (stageFilter === 'won' && !isLeadWon(status, lead.originalStatus)) {
          return false;
        }
        if (stageFilter === 'assigned' && status !== 'assigned') {
          return false;
        }
      }

      if (interestFilter !== 'all') {
        if ((lead.interestLevel || '').toLowerCase() !== interestFilter) {
          return false;
        }
      }

      return true;
    };

    leads.filter(matchesFilters).forEach((lead) => {
      const assignedName = (lead.assignedTo || 'Unassigned').trim();
      const normalisedName = TEAM_MEMBERS.includes(assignedName) ? assignedName : assignedName === 'Unassigned' ? 'Unassigned' : 'Other';

      if (!teamKeys.has('All Team Members') && !teamKeys.has(normalisedName)) {
        return;
      }

      const row = ensureRow(normalisedName);

      if (isLeadContacted(lead.stage, lead.originalStatus)) {
        row.leadsContacted += 1;
      }
      if (isLeadWon(lead.stage, lead.originalStatus)) {
        row.leadsWon += 1;
      }
      if (isLeadLost(lead.stage || lead.originalStatus)) {
        row.leadsLost += 1;
      }
    });

    // Ensure every selected team member appears at least once
    (selectedTeamMembers.includes('All Team Members') ? TEAM_MEMBERS.slice(1) : selectedTeamMembers).forEach((member) => {
      if (member === 'All Team Members') return;
      ensureRow(member);
    });

    const reportRows = Object.values(rows)
      .filter((row) => row.teamMember !== 'All Team Members')
      .map((row) => ({
        ...row,
        conversionRate: row.leadsContacted > 0 ? Number(((row.leadsWon / row.leadsContacted) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.conversionRate - a.conversionRate);

    if (!selectedTeamMembers.length || selectedTeamMembers.includes('All Team Members')) {
      const totals = reportRows.reduce(
        (acc, row) => ({
          leadsContacted: acc.leadsContacted + row.leadsContacted,
          leadsWon: acc.leadsWon + row.leadsWon,
          leadsLost: acc.leadsLost + row.leadsLost,
        }),
        { leadsContacted: 0, leadsWon: 0, leadsLost: 0 }
      );

      reportRows.unshift({
        teamMember: 'All Team Members',
        leadsContacted: totals.leadsContacted,
        leadsWon: totals.leadsWon,
        leadsLost: totals.leadsLost,
        conversionRate: totals.leadsContacted > 0 ? Number(((totals.leadsWon / totals.leadsContacted) * 100).toFixed(1)) : 0,
      });
    }

    return reportRows;
  }, [leads, selectedTeamMembers, stageFilter, interestFilter, startDate, endDate]);

  const reportRows = filteredReport.length ? filteredReport : DEMO_REPORT_ROWS;

  const nonAggregateRows = reportRows.filter((row) => row.teamMember !== 'All Team Members');
  const aggregatedRow = reportRows.find((row) => row.teamMember === 'All Team Members');

  const summaryTotals = nonAggregateRows.reduce(
    (acc, row) => ({
      leadsContacted: acc.leadsContacted + row.leadsContacted,
      leadsWon: acc.leadsWon + row.leadsWon,
      leadsLost: acc.leadsLost + row.leadsLost,
    }),
    { leadsContacted: 0, leadsWon: 0, leadsLost: 0 }
  );

  const summaryCards = [
    {
      title: 'Leads Contacted',
      value: summaryTotals.leadsContacted,
      subtext: 'Total follow-ups across the team',
      gradient: 'linear-gradient(135deg, #42a5f5 0%, #1e88e5 100%)',
    },
    {
      title: 'Leads Won',
      value: summaryTotals.leadsWon,
      subtext: 'Qualified handoffs completed',
      gradient: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
    },
    {
      title: 'Leads Lost',
      value: summaryTotals.leadsLost,
      subtext: 'Marked not interested or inactive',
      gradient: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
    },
    {
      title: 'Conversion Rate',
      value: aggregatedRow
        ? `${aggregatedRow.conversionRate.toFixed(1)}%`
        : summaryTotals.leadsContacted > 0
          ? `${((summaryTotals.leadsWon / summaryTotals.leadsContacted) * 100).toFixed(1)}%`
          : '0.0%',
      subtext: 'Won vs contacted leads',
      gradient: 'linear-gradient(135deg, #ab47bc 0%, #8e24aa 100%)',
    },
  ];

  const handleExport = () => {
    if (!reportRows.length) return;

    const headers = ['Team Member', 'Leads Contacted', 'Leads Won', 'Leads Lost', 'Conversion Rate'];
    const csvRows = [headers.join(',')];

    reportRows.forEach((row) => {
      csvRows.push([
        row.teamMember,
        row.leadsContacted,
        row.leadsWon,
        row.leadsLost,
        `${row.conversionRate}%`,
      ].join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sales-report-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Sales Performance Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Track lead outcomes by team member and export performance snapshots.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} md={6} lg={3} key={card.title}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 3,
                background: card.gradient,
                color: 'white',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  {card.title}
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {card.subtext}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.18)',
                  top: -60,
                  right: -60,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.12)',
                  bottom: -50,
                  left: -30,
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
         <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Team Members</InputLabel>
              <Select
                multiple
                value={selectedTeamMembers}
                onChange={(event) => {
                  let value = event.target.value as string[];
                  value = Array.isArray(value) ? value : [];

                  if (value.includes('All Team Members') && value.length > 1) {
                    value = value.filter((member) => member !== 'All Team Members');
                  }

                  if (value.length === 0) {
                    setSelectedTeamMembers(['All Team Members']);
                  } else if (value.length === 1 && value[0] === 'All Team Members') {
                    setSelectedTeamMembers(['All Team Members']);
                  } else {
                    setSelectedTeamMembers(value);
                  }
                }}
                input={<OutlinedInput label="Team Members" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {TEAM_MEMBERS.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={stageFilter}
                onChange={(event) => setStageFilter(event.target.value)}
              >
                {STAGE_FILTERS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth>
              <InputLabel>Interest</InputLabel>
              <Select
                label="Interest"
                value={interestFilter}
                onChange={(event) => setInterestFilter(event.target.value)}
              >
                {INTEREST_FILTERS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="date"
                label="From"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label="To"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={12} lg={'auto'}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh data">
                <span>
                  <IconButton onClick={handleRefresh} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reset filters">
                <span>
                  <IconButton
                    onClick={() => {
                      setSelectedTeamMembers(['All Team Members']);
                      setStageFilter('all');
                      setInterestFilter('all');
                      setStartDate('');
                      setEndDate('');
                    }}
                  >
                    <FilterListIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={!reportRows.length}
                sx={{ bgcolor: '#1a237e' }}
              >
                Export CSV
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a237e' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Team Member</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Leads Contacted</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Leads Won</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Leads Lost</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Conversion Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No leads match the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {reportRows.map((row) => (
                <TableRow
                  key={row.teamMember}
                  hover
                  sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9fb' } }}
                >
                  <TableCell sx={{ fontWeight: row.teamMember === 'All Team Members' ? 700 : 500 }}>
                    {row.teamMember}
                  </TableCell>
                  <TableCell align="right">{row.leadsContacted}</TableCell>
                  <TableCell align="right">{row.leadsWon}</TableCell>
                  <TableCell align="right">{row.leadsLost}</TableCell>
                  <TableCell align="right">{row.conversionRate}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Reports;
